import { Alert, Avatar, Box, Button, Card, CardContent, Divider, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";

import { User, updateProfile } from "firebase/auth";
import { tutorials } from "../config/tutorials";
import { useAuth } from "../hooks/useAuth";
import { usePageTutorial } from "../hooks/useTutorial";
import { auth } from "../services/firebase";
import { userProfileService } from "../services/UserProfileService";
import { DateUtils } from "../utils/dateUtils";
import { GoogleDriveFolderPicker } from "./GoogleDriveFolderPicker";
import { HelpIcon } from "./HelpIcon";
import { TimezoneSelector } from "./TimezoneSelector";
import { WorkingHoursSelector } from "./WorkingHoursSelector";

interface UserProfile {
  name?: string;
  company?: string;
  jobRole?: string;
  timezone?: string;
  workingHours?: {
    [key: string]: {
      start: string;
      end: string;
      enabled: boolean;
    };
  };
  bufferTime?: number;
  supportingDocsFolderId?: string;
  supportingDocsFolderName?: string;
  supportingDocsSourceName?: string;
}

export const ProfilePage: React.FC = () => {
  const { user: authUser } = useAuth();
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [jobRole, setJobRole] = useState("");
  const [timezone, setTimezone] = useState("");
  const [workingHours, setWorkingHours] = useState<{
    [key: string]: {
      start: string;
      end: string;
      enabled: boolean;
    };
  }>({});
  const [bufferTime, setBufferTime] = useState(15);
  const [supportingDocsFolderId, setSupportingDocsFolderId] = useState("");
  const [supportingDocsFolderName, setSupportingDocsFolderName] = useState("");
  const [supportingDocsSourceName, setSupportingDocsSourceName] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Initialize tutorial for this page
  usePageTutorial("profile_page_tour", tutorials.profile);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setUser(user);
      if (user) {
        setDisplayName(user.displayName || "");
      }
    });

    return () => unsubscribe();
  }, []);

  // Load custom user profile data
  useEffect(() => {
    const loadUserProfile = async () => {
      if (!authUser) return;

      try {
        const profile = await userProfileService.getUserProfile();
        setUserProfile(profile);
        setName(profile?.name || "");
        setCompany(profile?.company || "");
        setJobRole(profile?.jobRole || "");
        setTimezone(profile?.timezone || "");
        setWorkingHours(profile?.workingHours || {});
        setBufferTime(profile?.bufferTime || 15);
        setSupportingDocsFolderId(profile?.supportingDocsFolderId || "");
        setSupportingDocsFolderName(profile?.supportingDocsFolderName || "");
        setSupportingDocsSourceName(profile?.supportingDocsSourceName || "");
      } catch (error) {
        console.error("Error loading user profile:", error);
      }
    };

    loadUserProfile();
  }, [authUser]);

  const handleSaveProfile = async () => {
    if (!user) return;

    setIsLoading(true);
    setMessage(null);

    try {
      // Update Firebase Auth profile
      await updateProfile(user, {
        displayName: displayName,
      });

      // Update custom user profile
      await userProfileService.updateUserProfile({
        name: name,
        company: company,
        jobRole: jobRole,
        timezone: timezone,
        workingHours: workingHours,
        bufferTime: bufferTime,
        supportingDocsFolderId: supportingDocsFolderId || undefined,
        supportingDocsFolderName: supportingDocsFolderName || undefined,
        supportingDocsSourceName: supportingDocsSourceName || undefined,
      });

      setMessage({ type: "success", text: "Profile updated successfully!" });
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating profile:", error);
      setMessage({ type: "error", text: "Failed to update profile. Please try again." });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setDisplayName(user?.displayName || "");
    setName(userProfile?.name || "");
    setCompany(userProfile?.company || "");
    setJobRole(userProfile?.jobRole || "");
    setTimezone(userProfile?.timezone || "");
    setWorkingHours(userProfile?.workingHours || {});
    setBufferTime(userProfile?.bufferTime || 15);
    setSupportingDocsFolderId(userProfile?.supportingDocsFolderId || "");
    setSupportingDocsFolderName(userProfile?.supportingDocsFolderName || "");
    setSupportingDocsSourceName(userProfile?.supportingDocsSourceName || "");
    setIsEditing(false);
    setMessage(null);
  };

  const formatWorkingHours = () => {
    const days = [
      { key: "monday", label: "Monday" },
      { key: "tuesday", label: "Tuesday" },
      { key: "wednesday", label: "Wednesday" },
      { key: "thursday", label: "Thursday" },
      { key: "friday", label: "Friday" },
      { key: "saturday", label: "Saturday" },
      { key: "sunday", label: "Sunday" },
    ];

    return days.map((day) => {
      const dayHours = workingHours[day.key];
      if (!dayHours || !dayHours.enabled) {
        return { day: day.label, hours: "Not working" };
      }
      return { day: day.label, hours: `${dayHours.start} - ${dayHours.end}` };
    });
  };

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" color="text.secondary">
          Please sign in to view your profile
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Profile
        </Typography>
        <HelpIcon
          tutorialConfig={tutorials.profile}
          pageDescription="Manage your personal information, company details, and account settings. Keep your profile up to date to get the most personalized experience from Costaff.ai!"
        />
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }}>
          {message.text}
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
        <Paper sx={{ p: 3, flex: "1 1 600px" }} data-tutorial="profile-info">
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <Avatar src={user.photoURL || undefined} sx={{ width: 80, height: 80, mr: 3 }}>
              {user.displayName?.[0] || user.email?.[0]}
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {user.displayName || "User"}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {user.email}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ mb: 2 }}>
            Profile Information
          </Typography>

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <TextField label="Display Name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} disabled={!isEditing} sx={{ mb: 2, minWidth: 200 }} />
            <TextField label="Full Name" value={name} onChange={(e) => setName(e.target.value)} disabled={!isEditing} sx={{ mb: 2, minWidth: 200 }} />
            <TextField label="Company" value={company} onChange={(e) => setCompany(e.target.value)} disabled={!isEditing} sx={{ mb: 2, minWidth: 200 }} />
            <TextField label="Job Role" value={jobRole} onChange={(e) => setJobRole(e.target.value)} disabled={!isEditing} sx={{ mb: 2, minWidth: 200 }} />
            <TextField label="Email" value={user.email || ""} disabled sx={{ mb: 2, minWidth: 200 }} />
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ mb: 2 }}>
            Timezone & Working Hours
          </Typography>

          {!isEditing ? (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Timezone
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {timezone || "Not set"}
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Buffer Time
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {bufferTime} minutes
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Working Hours
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: 400 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Day</TableCell>
                        <TableCell>Hours</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formatWorkingHours().map((row) => (
                        <TableRow key={row.day}>
                          <TableCell>{row.day}</TableCell>
                          <TableCell>{row.hours}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </>
          ) : (
            <>
              <Box sx={{ mb: 3 }}>
                <TimezoneSelector
                  value={timezone}
                  onChange={setTimezone}
                  autoDetect={false}
                  label="Your Timezone"
                  helperText="This helps us display times correctly and suggest meeting times in your local time."
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <WorkingHoursSelector
                  value={workingHours}
                  onChange={setWorkingHours}
                  bufferTime={bufferTime}
                  onBufferTimeChange={setBufferTime}
                  showBufferTime={true}
                  helperText="Set your working hours for each day. This helps the AI assistant schedule meetings at the right times."
                />
              </Box>
            </>
          )}

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" sx={{ mb: 2 }}>
            Supporting Documents
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure a Google Drive folder containing supporting documents (e.g., sales materials, BD resources) that your coaches can access.
          </Typography>

          {!isEditing ? (
            <>
              {supportingDocsFolderName ? (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Folder
                  </Typography>
                  <Typography variant="body1">{supportingDocsFolderName}</Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No folder configured
                </Typography>
              )}
              {supportingDocsSourceName && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Source Name
                  </Typography>
                  <Typography variant="body1">{supportingDocsSourceName}</Typography>
                </Box>
              )}
            </>
          ) : (
            <>
              <Box sx={{ mb: 3 }}>
                <GoogleDriveFolderPicker
                  selectedFolderId={supportingDocsFolderId}
                  selectedFolderName={supportingDocsFolderName}
                  onFolderSelect={(folderId, folderName) => {
                    setSupportingDocsFolderId(folderId);
                    setSupportingDocsFolderName(folderName);
                  }}
                  disabled={!isEditing}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <TextField
                  label="Source Name"
                  value={supportingDocsSourceName}
                  onChange={(e) => setSupportingDocsSourceName(e.target.value)}
                  placeholder="e.g., Sales Docs, BD Materials"
                  disabled={!isEditing}
                  fullWidth
                  helperText="A descriptive name for this document source (used by coaches)"
                />
              </Box>
            </>
          )}

          <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
            {!isEditing ? (
              <Button variant="contained" onClick={() => setIsEditing(true)}>
                Edit Profile
              </Button>
            ) : (
              <>
                <Button variant="contained" onClick={handleSaveProfile} disabled={isLoading}>
                  {isLoading ? "Saving..." : "Save Changes"}
                </Button>
                <Button variant="outlined" onClick={handleCancel} disabled={isLoading}>
                  Cancel
                </Button>
              </>
            )}
          </Box>
        </Paper>

        <Card sx={{ flex: "0 1 300px" }} data-tutorial="profile-settings">
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Account Information
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Account Created
              </Typography>
              <Typography variant="body1">{user.metadata.creationTime ? DateUtils.formatForDisplay(new Date(user.metadata.creationTime)) : "Unknown"}</Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Last Sign In
              </Typography>
              <Typography variant="body1">{user.metadata.lastSignInTime ? DateUtils.formatForDisplay(new Date(user.metadata.lastSignInTime)) : "Unknown"}</Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Email Verified
              </Typography>
              <Typography variant="body1">{user.emailVerified ? "Yes" : "No"}</Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};
